import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { httpClient } from "api/httpClient";
import CodeDialog from "components/CodeDialog";
import { Datagrid } from "components/CustomizableDataGrid";
import { CopyableWrapper } from "components/fields/CopyableWrapper";
import { DateTimeField } from "components/fields/DateFormatField";
import { OrderReferenceField } from "components/fields/OrderReferenceField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { SsccFieldWithBarcode } from "components/fields/SsccFieldWithBarcode";
import { YesNoField } from "components/fields/YesNoField";
import { PalletsSummary } from "components/PalletsSummary";
import { envSettings } from "core/EnvSettings";
import { PalletType } from "entities/PalletType";
import { NotCustomerRoles } from "entities/UserRole";
import React from "react";
import {
	FunctionField,
	NumberField,
	ReferenceField,
	TextField,
	useListContext,
	usePermissions,
	useTranslate,
} from "react-admin";
import { isNullUndEmpty } from "utils/general";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";
import { formatWeight } from "utils/weight";

import { CopyableStorageLinkChip } from "../orders/Show/PalletsAndBoxes/components/StorageLinkChip";
import { useInventorySummary } from "./hooks/useInventorySummary";

const useStyles = makeStyles((theme) => ({
	centeredDatagrid: {
		// Center all cells except the expand column
		"& .RaDatagrid-cell:not(.RaDatagrid-expandIconCell)": {
			textAlign: "center !important",
		},
		// Center content within cells (except expand cell)
		"& .RaDatagrid-cell:not(.RaDatagrid-expandIconCell) > *": {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: "100%",
		},
		// Keep expand button on the left
		"& .RaDatagrid-expandIconCell": {
			textAlign: "left !important",
			width: "48px", // Fixed width for expand column
		},
		// Ensure expand button stays in place
		"& .RaDatagrid-expandIcon": {
			marginLeft: "8px",
		},
		// Center chips and buttons
		"& .MuiChip-root": {
			margin: "0 auto",
		},
		"& .MuiButton-root": {
			margin: "0 auto",
		},
		"& td": {
			textAlign: "center !important",
		},
		"& th": {
			textAlign: "center !important",
		},
	},
}));

const reservedColor = "#4caf5026";

const inventoryListRowStyle = (record) => {
	const isPalletFullyReserved = record.reserved_order_id;
	const bgColor = isPalletFullyReserved ? reservedColor : "inherit";
	return {
		height: "50px",
		backgroundColor: bgColor,
	};
};

export const InventoryListGrid = (props) => {
	const classes = useStyles();
	const { permissions } = usePermissions();
	const { data, ids, filterValues } = useListContext();
	const [storagePlaces, setStoragePlaces] = React.useState({});
	const [records, setRecords] = React.useState([]);
	const { summaryTotals } = useInventorySummary({ filterValues });

	const columnsAsyncStorage = useAsyncColumnsStorage("inventory-list-grid-columns-storage");

	const showCompanyColumn =
		filterValues?.company_id == null && NotCustomerRoles.includes(permissions?.role);

	// Barcode dialog
	const [barcodeDialogOpen, setBarcodeDialogOpen] = React.useState(false);
	const [barcodeValue, setBarcodeValue] = React.useState("");
	const [barcodeTitle, setBarcodeTitle] = React.useState("");
	const translate = useTranslate();

	React.useEffect(() => {
		if (!data || !ids?.length) {
			setRecords([]);
			return;
		}
		setRecords(ids.map((id) => data[id]));
	}, [data, ids]);

	// Fetch storage places when storage module is enabled
	React.useEffect(() => {
		if (!envSettings.REACT_APP_FF_STORAGE_MODULE || !records?.length) {
			return;
		}

		const uniqueStoragePlaceIds = [
			...new Set(records.map((p) => p.storage_place_id).filter((id) => !isNullUndEmpty(id))),
		];

		if (uniqueStoragePlaceIds.length === 0) {
			setStoragePlaces({});
			return;
		}

		const storagePlaceFilter = JSON.stringify({
			id: uniqueStoragePlaceIds,
			no_range: true,
		});
		const storagePlaceUrl = `/storage-places-lookup?filter=${encodeURIComponent(
			storagePlaceFilter
		)}`;

		httpClient(storagePlaceUrl)
			.then(({ json: storagePlacesData }) => {
				const storagePlacesMap = {};
				storagePlacesData.forEach((sp) => {
					storagePlacesMap[sp.id] = sp;
				});
				setStoragePlaces(storagePlacesMap);
			})
			.catch((err) => {
				console.error("Failed to load storage places:", err);
				setStoragePlaces({});
			});
	}, [records]);

	return (
		<>
			{summaryTotals && <PalletsSummary summaryTotals={summaryTotals} />}
			<Box sx={{ overflow: "auto", maxWidth: "100%", maxHeight: "65vh" }}>
				<Datagrid
					{...props}
					className={classes.centeredDatagrid}
					isRowSelectable={false}
					rowClick={null}
					bulkActionButtons={false}
					storage={columnsAsyncStorage}
					rowStyle={inventoryListRowStyle}
					reorderingSupport={true}
				>
					<OrderReferenceField
						source="order_id"
						label={translate("resources.inventory-list.fields.order_reference")}
					/>

					<CopyableWrapper
						source="batch_number"
						label={translate("tab.pallets.batch_number")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="lot_number"
						label={translate("tab.pallets.lot_number")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="packing_station"
						label={translate("tab.pallets.packing_station")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="ceel_number"
						label={translate("tab.pallets.ceel_number")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="product_number"
						label={translate("tab.pallets.product_number")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>
					<CopyableWrapper
						source="product_name"
						label={translate("tab.pallets.product_name")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<SelectEnumField
						source="type"
						enumName="pallet_type"
						enumObj={PalletType}
						label={translate("tab.pallets.type")}
						sortable={true}
					/>

					<NumberField
						source="quantity"
						label={translate("tab.pallets.quantity")}
						sortable={true}
					/>
					<FunctionField
						source="net_weight"
						label={translate("tab.pallets.weight_net")}
						render={(record) => formatWeight(record?.net_weight, record?.weight_unit_code)}
						sortable={true}
					/>

					<CopyableWrapper
						source="production_datetime"
						label={translate("tab.pallets.production_datetime")}
						sortable={true}
					>
						<DateTimeField />
					</CopyableWrapper>
					<CopyableWrapper
						source="expiration_datetime"
						label={translate("tab.pallets.expiration_datetime")}
						sortable={true}
					>
						<DateTimeField />
					</CopyableWrapper>

					<FunctionField
						source="storage_place_id"
						label={translate(
							`tab.pallets.${
								envSettings.REACT_APP_FF_STORAGE_MODULE ? "storage_place_id" : "area_id"
							}`
						)}
						render={(record) => (
							<CopyableStorageLinkChip
								pallet={record}
								terminalId={record.terminal_id}
								storagePlaces={storagePlaces}
							/>
						)}
					/>

					<CopyableWrapper source="id" label={translate("tab.pallets.pallet_id")} sortable={true}>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper source="sscc" label={translate("tab.pallets.sscc")} sortable={true}>
						<SsccFieldWithBarcode
							setBarcodeValue={setBarcodeValue}
							setBarcodeTitle={setBarcodeTitle}
							setBarcodeDialogOpen={setBarcodeDialogOpen}
						/>
					</CopyableWrapper>

					<CopyableWrapper
						source="sscc_original"
						label={translate("tab.pallets.sscc_original")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper source="gtin" label={translate("tab.pallets.gtin")} sortable={true}>
						<TextField />
					</CopyableWrapper>

					<CopyableWrapper
						source="ext_id"
						label={translate("tab.pallets.external_id")}
						sortable={true}
					>
						<TextField />
					</CopyableWrapper>

					<YesNoField source="is_t1" label={translate("tab.pallets.is_t1")} sortable={true} />

					<ReferenceField
						source="terminal_id"
						reference="terminal"
						label={translate("resources.inventory-list.fields.terminal_id")}
						link={false}
						sortable={false}
					>
						<TextField source="name" />
					</ReferenceField>

					{showCompanyColumn && (
						<ReferenceField
							source="company_id"
							reference="customer-companies"
							label={translate("resources.inventory-list.fields.company_id")}
							link={false}
						>
							<TextField source="short_name" />
						</ReferenceField>
					)}

					<TextField
						source="original_quantity"
						label={translate("resources.inventory-list.fields.original_quantity")}
						sortable={true}
						defaultHidden
					/>
					<TextField
						source="box_weight"
						label={translate("resources.inventory-list.fields.box_weight")}
						sortable={true}
						defaultHidden
					/>
				</Datagrid>
			</Box>
			{/* Barcode Dialog */}
			<CodeDialog
				open={barcodeDialogOpen}
				type="barcode"
				onClose={() => setBarcodeDialogOpen(false)}
				value={barcodeValue}
				title={barcodeTitle || "SSCC Barcode"}
			/>
		</>
	);
};
